import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { ExpandMore } from '@mui/icons-material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { Page, Section, Info } from '../../components/DocsComponents.jsx'
import permissionDetails from './permissionDetails'

// Interactive permissions reference: every group below is data-driven from
// permissionDetails.js. The search box matches action names and role names;
// the role chips filter the whole matrix to actions that role can perform.

const Permissions = () => {
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState(null)

  const allRoles = useMemo(() => {
    const roles = new Set()
    permissionDetails.forEach((group) =>
      (group.rows || []).forEach((row) =>
        (row.roles || []).forEach((r) => roles.add(r)),
      ),
    )
    return [...roles].sort()
  }, [])

  const filtering = Boolean(query.trim()) || Boolean(roleFilter)

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase()

    return permissionDetails
      .map((group) => {
        const rows = (group.rows || []).filter((row) => {
          if (roleFilter && !(row.roles || []).includes(roleFilter))
            return false
          if (!q) return true

          return (
            row.action.toLowerCase().includes(q) ||
            (row.note || '').toLowerCase().includes(q) ||
            (row.roles || []).some((r) => r.toLowerCase().includes(q)) ||
            group.title.toLowerCase().includes(q)
          )
        })

        return { ...group, rows }
      })
      .filter((group) => group.rows.length > 0 || !filtering)
  }, [query, roleFilter, filtering])

  return (
    <Page
      title="Permissions & Roles"
      intro="Access in AmbuNet is controlled by the teams and roles on each employee's record, plus a few special roles. Use the search box or click a role to see exactly what it can do."
    >
      <Section title="Find a permission">
        <TextField
          size="small"
          fullWidth
          placeholder="Search actions or roles — e.g. safeguarding, payroll, invoices…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {allRoles.map((role) => (
            <Chip
              key={role}
              label={role}
              size="small"
              color="primary"
              variant={roleFilter === role ? 'filled' : 'outlined'}
              onClick={() =>
                setRoleFilter((current) => (current === role ? null : role))
              }
            />
          ))}
        </Stack>
        {filtering && (
          <Typography variant="body2" color="text.secondary">
            Showing{' '}
            {filteredGroups.reduce((sum, g) => sum + g.rows.length, 0)}{' '}
            matching action(s)
            {roleFilter ? ` for the ${roleFilter} role` : ''}. Click the role
            chip again or clear the search to reset.
          </Typography>
        )}
      </Section>

      <Info title="Where roles are set">
        Teams and roles are assigned by the Personnel team — per employee on
        their record, or in bulk from the <strong>Manage Access</strong> matrix
        under <strong>Site Config → Employees → Manage Access</strong>, which
        also sets the PTS Co-Ordinator flag and Command &amp; Control levels
        (you cannot change your own access there). A few actions are reserved
        for the Site Admin (the account that owns your organisation).
      </Info>

      <Stack gap={1}>
        {filteredGroups.map((x) => (
          <Accordion
            key={x.title}
            defaultExpanded
            expanded={filtering ? true : undefined}
          >
            <AccordionSummary
              sx={{ backgroundColor: 'primary.main', color: 'white' }}
              expandIcon={<ExpandMore sx={{ color: 'white' }} />}
            >
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                justifyContent="space-between"
                flex={1}
                pr={1}
              >
                {/* Explicit colour: the theme gives Typography its own dark
                    colour, which would otherwise render invisibly against the
                    dark summary bar. */}
                <Typography color="white">{x.title}</Typography>
                {filtering && (
                  <Chip
                    size="small"
                    label={x.rows.length}
                    sx={{ backgroundColor: 'white' }}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <Stack>
                {x.topComponent && x.topComponent}
                <CustomDataGrid
                  rows={x.rows ?? []}
                  getRowId={(row) => row.action ?? ''}
                  getRowHeight={() => 'auto'}
                  columns={[
                    {
                      field: 'action',
                      headerName: 'Action',
                      minWidth: 100,
                      flex: 1,
                      renderCell: ({ row }) => (
                        <Stack py={1}>
                          <Typography variant="body2">{row.action}</Typography>
                          {row.note && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.note}
                            </Typography>
                          )}
                        </Stack>
                      ),
                    },
                    {
                      field: 'roles',
                      headerName: 'Required Roles',
                      minWidth: 100,
                      flex: 1,
                      renderCell: ({ value }) => (
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          gap={0.5}
                          py={1}
                          alignItems="center"
                        >
                          {(value || []).map((v, index) => (
                            <Chip
                              key={index}
                              label={v}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => setRoleFilter(v)}
                            />
                          ))}
                        </Stack>
                      ),
                    },
                  ]}
                  disableRowSelectionOnClick
                  slots={{
                    toolbar: () => null,
                    footer: () => null,
                  }}
                  sx={{
                    borderRadius: 0,
                    '& .MuiDataGrid-columnHeaders': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-main': {
                      border: 'solid 1px',
                      borderRadius: '0 0 4px 4px',
                      borderTop: 0,
                    },
                  }}
                  color="greyContrast"
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Page>
  )
}

export default Permissions
